import { useEffect, useState, useCallback } from 'react';
import { doc, onSnapshot, setDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../../providers/firebase/firebase';
import type { TraderSubscriptionDoc } from '../../types/notifications';
import type { AlertSeverity } from '../../pages/markets/api/types';

interface SubscribeFields {
    min_severity: AlertSeverity;
    trader_label?: string;
}

interface UseTraderSubscriptionResult {
    subscription: TraderSubscriptionDoc | null;
    loading: boolean;
    /** True once auth has resolved to a signed-in user. */
    loggedIn: boolean;
    subscribe: (fields: SubscribeFields) => Promise<void>;
    update: (fields: Partial<Pick<TraderSubscriptionDoc, 'min_severity' | 'enabled'>>) => Promise<void>;
    unsubscribe: () => Promise<void>;
}

export function useTraderSubscription(address: string): UseTraderSubscriptionResult {
    const [uid, setUid] = useState<string | null>(null);
    const [subscription, setSubscription] = useState<TraderSubscriptionDoc | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const auth = getAuth();
        if (auth.currentUser) setUid(auth.currentUser.uid);
        return onAuthStateChanged(auth, u => setUid(u?.uid ?? null));
    }, []);

    useEffect(() => {
        // No signed-in user: stop loading so the button renders (it nudges the
        // user to sign in on click) instead of staying hidden forever.
        if (!uid) {
            setSubscription(null);
            setLoading(false);
            return;
        }
        if (!address) return;
        const ref = doc(db, `users/${uid}/trader_subscriptions/${address}`);
        const unsub = onSnapshot(ref, snap => {
            setSubscription(snap.exists() ? (snap.data() as TraderSubscriptionDoc) : null);
            setLoading(false);
        });
        return () => unsub();
    }, [uid, address]);

    const subscribe = useCallback(async (fields: SubscribeFields) => {
        if (!uid) return;
        await setDoc(doc(db, `users/${uid}/trader_subscriptions/${address}`), {
            trader_address: address,
            trader_label: fields.trader_label ?? address,
            created_at: serverTimestamp(),
            min_severity: fields.min_severity,
            enabled: true,
        });
    }, [uid, address]);

    const update = useCallback(async (fields: Partial<Pick<TraderSubscriptionDoc, 'min_severity' | 'enabled'>>) => {
        if (!uid) return;
        await updateDoc(doc(db, `users/${uid}/trader_subscriptions/${address}`), { ...fields });
    }, [uid, address]);

    const unsubscribe = useCallback(async () => {
        if (!uid) return;
        await deleteDoc(doc(db, `users/${uid}/trader_subscriptions/${address}`));
    }, [uid, address]);

    return { subscription, loading, loggedIn: !!uid, subscribe, update, unsubscribe };
}
